import { cn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { FileText as FileTextIcon, type Icon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useState } from "react";
import { useParams } from "react-router";

export type Recipient = { teamId: number; name: string };

/** Document à imprimer pour un bordereau, avec ses copies papier. */
export type PieceDocument = {
  documentVersionId: number;
  authorCompanyName: string | null;
  lot: string | null;
  number: string | null;
  name: string | null;
  index: string | null;
  quantity: number;
  fichierPdfId: number | null;
};

export type PendingDispatchItem = {
  slipId: number;
  number: string | null;
  createdAt: string;
  authorName: string | null;
  pendingCount: number;
  documentCount: number;
  // Formats réellement persistés (version_transmission.native / .pdf). Présents
  // uniquement quand l'écran les demande (remise en main propre).
  nativeCount?: number;
  pdfCount?: number;
  recipients: Recipient[];
  documents: PieceDocument[];
};

export type PendingDispatchPayload = {
  items: PendingDispatchItem[];
  recipients: Recipient[];
  counts: { do: number; presta: number };
  total: number;
};

export type Target = "do" | "presta";

export type PendingDispatchFetchArgs = {
  yardId: string;
  target: Target;
  recipientTeamId: number | null;
};

export type PendingDispatchConfig = {
  /** Titre de l'écran. */
  title: string;
  /** Message d'erreur de chargement. */
  errorLabel: string;
  /** Message d'état vide. */
  emptyLabel: string;
  /** Intitulé de la colonne « exemplaires en attente ». */
  pendingColumnLabel: string;
  /** Picto affiché sur le compteur d'onglet et la colonne « en attente ». */
  pendingIcon: Icon;
  /** Info-bulle du badge d'onglet, selon le nombre de bordereaux. */
  tabBadgeTitle: (count: number) => string;
  /** Affiche la colonne « Formats » (natif / PDF réellement transmis). */
  showFormats?: boolean;
  /** Remplace le tableau récapitulatif (écran 021 : pièces à réunir). */
  renderTable?: (args: {
    loading: boolean;
    items: PendingDispatchItem[];
  }) => ReactNode;
  /**
   * Charge la file pour la cible/le destinataire courant. Doit lever une
   * exception en cas d'échec (réseau ou erreur applicative) : l'écran bascule
   * alors sur l'état d'erreur.
   */
  fetchItems: (
    args: PendingDispatchFetchArgs,
  ) => Promise<PendingDispatchPayload>;
};

type FetchState =
  | { status: "loading" }
  | { status: "error" }
  | { status: "success"; payload: PendingDispatchPayload };

const TARGET_LABELS: Record<Target, string> = {
  do: "Donneurs d'ordre",
  presta: "Prestataires",
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("fr-FR");
}

/**
 * Écrans 021 / 021bis — file en lecture seule des bordereaux ayant des envois
 * physiques en attente (mise au courrier ou remise en main propre), répartis
 * par cible (donneurs d'ordre / prestataires) avec compteur par onglet et
 * filtre par destinataire. Par défaut, un récapitulatif par bordereau : date de
 * création, auteur, n° de bordereau, nombre d'exemplaires en attente, nombre
 * de documents et — pour la remise en main propre — les formats transmis
 * (natif / PDF). `renderTable` le remplace (écran 021 : pièces à réunir).
 */
export function PendingDispatchScreen(config: PendingDispatchConfig) {
  const { yardId } = useParams<{ yardId: string }>();
  const [target, setTarget] = useState<Target>("do");
  const [recipientTeamId, setRecipientTeamId] = useState<number | null>(null);
  const [state, setState] = useState<FetchState>({ status: "loading" });
  const { fetchItems } = config;

  useEffect(() => {
    if (isNil(yardId)) return;

    let cancelled = false;

    async function run() {
      setState({ status: "loading" });
      try {
        const payload = await fetchItems({
          yardId: yardId ?? "",
          target,
          recipientTeamId,
        });
        if (cancelled) return;
        setState({ status: "success", payload });
      } catch {
        if (cancelled) return;
        setState({ status: "error" });
      }
    }

    run();
    return () => {
      cancelled = true;
    };
  }, [yardId, target, recipientTeamId, fetchItems]);

  const payload = state.status === "success" ? state.payload : null;
  const items = payload?.items ?? [];
  const counts = payload?.counts ?? { do: 0, presta: 0 };
  const recipients = payload?.recipients ?? [];
  const showFormats = config.showFormats ?? false;
  const PendingIcon = config.pendingIcon;
  const columnCount = showFormats ? 6 : 5;

  function selectTarget(next: Target) {
    if (next === target) return;
    // Les options de destinataires diffèrent selon la cible : on réinitialise
    // le filtre pour éviter un id absent de la nouvelle liste.
    setRecipientTeamId(null);
    setTarget(next);
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="border-b border-zinc-200 px-4 py-4 dark:border-zinc-700">
        <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
          {config.title}
        </h2>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          {(["do", "presta"] as const).map((t) => {
            const isActive = t === target;
            const count = counts[t];
            return (
              <button
                key={t}
                type="button"
                onClick={() => selectTarget(t)}
                className={cn(
                  "inline-flex items-center gap-2 rounded-md border px-3 py-1.5 text-sm",
                  isActive
                    ? "border-blue-500 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-950 dark:text-blue-300"
                    : "border-zinc-200 text-zinc-700 hover:bg-zinc-100 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800",
                )}
              >
                {TARGET_LABELS[t]}
                <span
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs",
                    count > 0
                      ? "bg-blue-600 text-white"
                      : "bg-zinc-200 text-zinc-500 dark:bg-zinc-700 dark:text-zinc-400",
                  )}
                  title={config.tabBadgeTitle(count)}
                >
                  <PendingIcon size={14} weight="fill" />
                  {count}
                </span>
              </button>
            );
          })}

          <div className="ml-auto flex items-center gap-2">
            <label htmlFor="recipient-filter" className="text-sm text-zinc-500">
              Destinataire
            </label>
            <select
              id="recipient-filter"
              value={recipientTeamId ?? ""}
              onChange={(e) =>
                setRecipientTeamId(
                  e.target.value === "" ? null : Number(e.target.value),
                )
              }
              className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-sm text-zinc-700 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300"
            >
              <option value="">Tous</option>
              {recipients.map((r) => (
                <option key={r.teamId} value={r.teamId}>
                  {r.name}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {state.status === "error" ? (
        <div className="flex flex-1 items-center justify-center">
          <p className="text-red-600">{config.errorLabel}</p>
        </div>
      ) : isNotNil(config.renderTable) ? (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          {config.renderTable({ loading: state.status === "loading", items })}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-hidden p-4">
          <div className="h-full overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
            {/*
              Règles écran : table à disposition fixe, colonne « Bordereau »
              élastique, colonnes fixes centrées, hauteur de ligne fixe avec
              troncature (texte complet au survol), seul le corps de table
              défile verticalement sous un en-tête collant.
            */}
            <table className="w-full table-fixed border-collapse text-sm">
              <colgroup>
                <col className="w-32" />
                <col />
                <col className="w-48" />
                <col className="w-32" />
                <col className="w-28" />
                {showFormats ? <col className="w-32" /> : null}
              </colgroup>
              <thead className="sticky top-0 z-10 bg-zinc-50 dark:bg-zinc-900">
                <tr className="border-b border-zinc-200 dark:border-zinc-800">
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Date de création
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Bordereau
                  </th>
                  <th className="truncate px-3 py-2 text-left font-medium text-zinc-700 dark:text-zinc-300">
                    Auteur
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    {config.pendingColumnLabel}
                  </th>
                  <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                    Documents
                  </th>
                  {showFormats ? (
                    <th className="truncate px-3 py-2 text-center font-medium text-zinc-700 dark:text-zinc-300">
                      Formats
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {state.status === "loading" ? (
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="px-3 py-8 text-center text-sm text-zinc-500"
                    >
                      Chargement...
                    </td>
                  </tr>
                ) : items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={columnCount}
                      className="px-3 py-12 text-center text-sm text-zinc-500"
                    >
                      {config.emptyLabel}
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const label = item.number ?? `Bordereau ${item.slipId}`;
                    const recipientLabel = item.recipients
                      .map((r) => r.name)
                      .join(", ");
                    const nativeCount = item.nativeCount ?? 0;
                    const pdfCount = item.pdfCount ?? 0;
                    return (
                      <tr
                        key={item.slipId}
                        className="h-10 border-b border-zinc-100 last:border-0 dark:border-zinc-800"
                      >
                        <td className="truncate px-3 text-left text-zinc-700 dark:text-zinc-300">
                          {formatDate(item.createdAt)}
                        </td>
                        <td className="truncate px-3 text-left">
                          <span title={recipientLabel || label}>{label}</span>
                        </td>
                        <td className="truncate px-3 text-left text-zinc-700 dark:text-zinc-300">
                          <span title={item.authorName ?? undefined}>
                            {item.authorName ?? "-"}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 text-center">
                          <span
                            className="inline-flex items-center justify-center gap-1 text-zinc-700 dark:text-zinc-300"
                            title={recipientLabel || undefined}
                          >
                            <PendingIcon size={16} weight="fill" />
                            {item.pendingCount}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 text-center">
                          {item.documentCount > 0 ? (
                            <span className="inline-flex items-center justify-center gap-1 text-zinc-700 dark:text-zinc-300">
                              <FileTextIcon size={16} weight="fill" />
                              {item.documentCount}
                            </span>
                          ) : (
                            <span className="text-zinc-300">-</span>
                          )}
                        </td>
                        {showFormats ? (
                          <td className="truncate px-3 text-center text-zinc-700 dark:text-zinc-300">
                            {nativeCount > 0 || pdfCount > 0 ? (
                              <span
                                title={`${nativeCount} natif(s) · ${pdfCount} PDF`}
                              >
                                {nativeCount} natif · {pdfCount} PDF
                              </span>
                            ) : (
                              <span className="text-zinc-300">-</span>
                            )}
                          </td>
                        ) : null}
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
